import { useEffect, useState, type Dispatch, type SetStateAction } from "react";

/**
 * Read the persisted collapse state for `storageKey`, falling back to `fallback` when there
 * is nothing stored (or storage is unreachable).
 *
 * ⚠️ The fallback is what carries D2, and it is why this is not a bare
 * `getItem(key) === "true"`. Only an explicitly stored value overrides the audience
 * default, so the SAME reader gives "absent ⇒ collapsed" for the patient key (default
 * `true`) and "absent ⇒ expanded" for the staff key (default `false`) — i.e. the patient
 * key's rule is `stored !== "false"`, expressed as a fallback rather than as a comparison
 * that has to be remembered to invert per key. Hard-coding `=== "true"` for both would
 * silently drop collapsed-by-default while still passing the "a patient who expanded stays
 * expanded" case, which is exactly the kind of half-right that survives review.
 *
 * Guards follow `readCollapsedState` in `shared/MetaPanel.tsx`: `window` existence plus a
 * try/catch on the read. A SecurityError (Safari private browsing, blocked third-party
 * storage) must not crash the shell — essentially every page imports this component, so a
 * throw here is a whole-app white screen over a cosmetic preference.
 */
function readCollapsed(storageKey: string, fallback: boolean): boolean {
	if (typeof window === "undefined") return fallback;
	try {
		const raw = window.localStorage.getItem(storageKey);
		if (raw === "true") return true;
		if (raw === "false") return false;
		return fallback;
	} catch {
		return fallback;
	}
}

// CU-14ygtkj8nr3: moved out of Layout.tsx so the Customer shell persists under its own key with identical semantics.
export function useSidebarCollapse(storageKey: string, fallback: boolean): [boolean, Dispatch<SetStateAction<boolean>>] {
	// A small local initialiser rather than MetaPanel's `useMetaPanelCollapse`: that hook
	// takes a key but no per-key default, and stores a Record<string, boolean> of section
	// names rather than one boolean. Reusing it would mean encoding the sidebar as a
	// single-entry map and then re-deriving the default outside it anyway. Same guards,
	// less ceremony.
	const [collapsed, setCollapsed] = useState(() => readCollapsed(storageKey, fallback));

	useEffect(() => {
		if (typeof window === "undefined") return;
		try {
			window.localStorage.setItem(storageKey, String(collapsed));
		} catch {
			/* ignore write errors — collapse state is a convenience, not data */
		}
		// `storageKey` is in the deps deliberately, not just `collapsed`: no caller flips
		// `isPatient` mid-mount today (patient Dashboard renders two <Layout isPatient> in
		// two branches), but one that did would otherwise keep writing the patient's state
		// into the practitioner's key for the rest of the mount.
	}, [collapsed, storageKey]);

	return [collapsed, setCollapsed];
}
